import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EntryFeedback } from '../conversationSlice';
import { isSameCompetitorAlert, withoutDuplicates } from '../../utils/sessionHistory';
import { relabelMovedAlerts } from '../../utils/participantLinesMoved';
import { relabelAlertsOf } from '../../utils/participantRoleChange';
import { participantLinesMoved, participantReclassified } from '../participantActions';

interface CompetitorAlert {
  competitor: string;
  matched_text: string;
  confidence: number;
  match_type: string;
  advantages: Array<{ point: string; context?: string }>;
  weaknesses: Array<{ point: string; reason?: string }>;
  talking_points: string;
  category: string;
  detected_at: string;
  timestamp?: string;
  speaker: string;
  /** Who said the line that raised it (standalone calls send it). */
  participantId?: string;
  speakerName?: string;
  feedback?: EntryFeedback;
  /** Loaded from the saved session (history restore): shown muted, marked as earlier. */
  isRestored?: boolean;
}

/** A competitor alert as the store holds it (live or restored). */
export type CompetitorAlertRecord = CompetitorAlert;

interface CompetitorState {
  alerts: CompetitorAlert[]; // Current alerts for display
  allAlerts: CompetitorAlert[]; // All accumulated alerts for session saving
  isConnected: boolean;
  connectionError: string | null;
}

const initialState: CompetitorState = {
  alerts: [],
  allAlerts: [], // Store all alerts for session saving
  isConnected: false,
  connectionError: null
};

const competitorSlice = createSlice({
  name: 'competitor',
  initialState,
  reducers: {
    addCompetitorAlert: {
      reducer: (state, action: PayloadAction<CompetitorAlert>) => {
        // Already shown as history: one card, and the restored one stays quiet.
        if (state.allAlerts.some((existing) => existing.isRestored && isSameCompetitorAlert(existing, action.payload))) return;
        // Always recorded for the session: the name was said.
        state.allAlerts.push(action.payload);
        // Repeats are gated server-side (ALERT_REPEAT_COOLDOWN_SECONDS in the
        // landmine and competitor services), so every alert that arrives is shown.
        state.alerts = [action.payload, ...state.alerts];
      },
      prepare: (alert: CompetitorAlert) => ({
        payload: alert.detected_at ? alert : { ...alert, detected_at: new Date().toISOString() },
      }),
    },
    /**
     * History restore: the saved session's competitor alerts, minus any
     * already here, marked `isRestored`. The card list is newest first, so the
     * earlier ones go after every card already shown.
     */
    restoreCompetitorHistory: (state, action: PayloadAction<CompetitorAlert[]>) => {
      const missing = withoutDuplicates(action.payload || [], state.allAlerts, isSameCompetitorAlert);
      const restored = missing.map((alert) => ({ ...alert, isRestored: true }));
      state.allAlerts.push(...restored);
      state.alerts.push(...[...restored].reverse());
    },
    removeCompetitorAlert: (state, action: PayloadAction<number>) => {
      state.alerts.splice(action.payload, 1);
    },
    clearCompetitorAlerts: (state) => {
      state.alerts = [];
      state.allAlerts = [];
    },
    setCompetitorConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      if (action.payload) {
        state.connectionError = null;
      }
    },
    setCompetitorConnectionError: (state, action: PayloadAction<string | null>) => {
      state.connectionError = action.payload;
    },
    updateCompetitorFeedback: (state, action: PayloadAction<{ detected_at: string; feedback: EntryFeedback }>) => {
      const updateIn = (arr: CompetitorAlert[]) => {
        const alert = arr.find(a => a.detected_at === action.payload.detected_at);
        if (alert) alert.feedback = action.payload.feedback;
      };
      updateIn(state.alerts);
      updateIn(state.allAlerts);
    }
  },
  extraReducers: (builder) => {
    // Raised by a line that moved to a newcomer: relabelled, never removed.
    builder.addCase(participantLinesMoved, (state, action) => {
      state.alerts = relabelMovedAlerts(state.alerts, action.payload);
      state.allAlerts = relabelMovedAlerts(state.allAlerts, action.payload);
    });
    // A human decided who the raiser is: their alerts take the role and name, never removed.
    builder.addCase(participantReclassified, (state, action) => {
      state.alerts = relabelAlertsOf(state.alerts, action.payload);
      state.allAlerts = relabelAlertsOf(state.allAlerts, action.payload);
    });
  }
});

export const {
  addCompetitorAlert,
  restoreCompetitorHistory,
  removeCompetitorAlert,
  clearCompetitorAlerts,
  setCompetitorConnectionStatus,
  setCompetitorConnectionError,
  updateCompetitorFeedback
} = competitorSlice.actions;

export default competitorSlice.reducer;